import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import PageTitle from "../../../layouts/PageTitle";
import { useGetLeaveByIdQuery, appApi } from "../../../../store/appApi";
import { updateLeaveStatusApi, deleteLeaveApi } from "../../../../services/leave";
import { selectUserRole } from "../../../../store/authSlice";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const STATUS_BADGE = { pending: "warning", approved: "success", rejected: "danger" };

// Clickable selfie thumbnail with lightbox
const SelfieThumb = ({ url, label }) => {
  const [open, setOpen] = useState(false);
  if (!url) return null;
  const src = url.startsWith("http") ? url : `${BASE_URL}${url}`;
  return (
    <>
      <img
        src={src}
        alt={label}
        onClick={() => setOpen(true)}
        style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, cursor: "pointer", border: "2px solid #dee2e6" }}
        title={`Click to enlarge — ${label}`}
      />
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, cursor: "zoom-out",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <img src={src} alt={label} style={{ maxHeight: "80vh", maxWidth: "90vw", borderRadius: 8 }} />
            <div style={{ color: "#fff", marginTop: 8, fontSize: 13 }}>{label}</div>
          </div>
        </div>
      )}
    </>
  );
};

const LeaveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector(selectUserRole);
  const isAdmin = ["super-admin", "Project Manager", "Admin"].includes(role);

  const { data, isLoading } = useGetLeaveByIdQuery(id);
  const leave = data?.leave;

  const handleStatus = async (newStatus) => {
    const confirmed = await Swal.fire({
      title: `${newStatus === "approved" ? "Approve" : "Reject"} this leave?`,
      icon: "question",
      showCancelButton: true,
    });
    if (!confirmed.isConfirmed) return;
    try {
      await updateLeaveStatusApi(id, newStatus);
      dispatch(appApi.util.invalidateTags(["Leaves"]));
      navigate("/staff/leave/list");
    } catch {
      Swal.fire("Error", "Action failed.", "error");
    }
  };

  const handleCancel = async () => {
    const confirmed = await Swal.fire({
      title: "Cancel this leave?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });
    if (!confirmed.isConfirmed) return;
    try {
      await deleteLeaveApi(id);
      dispatch(appApi.util.invalidateTags(["Leaves"]));
      navigate("/staff/leave/list");
    } catch {
      Swal.fire("Error", "Could not cancel.", "error");
    }
  };

  if (isLoading) return <div className="text-center py-5">Loading…</div>;
  if (!leave) return <div className="text-center py-5 text-muted">Leave not found.</div>;

  // Build day list for the leave period
  const calDays = [];
  const cur = new Date(leave.leaveFrom);
  const end = new Date(leave.leaveTo);
  while (cur <= end) {
    const key = [
      cur.getUTCFullYear(),
      String(cur.getUTCMonth() + 1).padStart(2, "0"),
      String(cur.getUTCDate()).padStart(2, "0"),
    ].join("-");
    const dow = cur.getUTCDay();
    calDays.push({ date: new Date(cur), key, isWeekend: dow === 0 || dow === 6 });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }

  const attendance = leave.attendance || {};

  return (
    <>
      <PageTitle activeMenu="Leave Detail" motherMenu="Leave" />
      <div className="row">
        {/* ── Left: Leave info ──────────────────────────────────────────── */}
        <div className="col-xl-7">
          <div className="card">
            <div className="card-header d-flex align-items-center">
              <button className="btn btn-sm btn-outline-secondary me-3" onClick={() => navigate(-1)}>
                ← Back
              </button>
              <h4 className="card-title mb-0">Leave Detail</h4>
              <div className="ms-auto d-flex gap-2 align-items-center">
                <span className={`badge bg-${STATUS_BADGE[leave.status] || "secondary"} text-capitalize`}>
                  {leave.status}
                </span>
                {leave.attendanceStatus && (
                  <span className={`badge bg-${leave.attendanceStatus === "half-day" ? "warning" : "success"}`}>
                    {leave.attendanceStatus === "half-day" ? "⚑ Half Day Present" : "✓ Was Present"}
                  </span>
                )}
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {leave.employee?.name && (
                  <div className="col-12">
                    <small className="text-muted">Employee</small>
                    <p className="fw-bold mb-0">{leave.employee.name}
                      {leave.employee.phone && <span className="text-muted fw-normal ms-2">· {leave.employee.phone}</span>}
                    </p>
                  </div>
                )}
                <div className="col-6">
                  <small className="text-muted">Leave Type</small>
                  <p className="fw-bold mb-0">{leave.leaveType}</p>
                </div>
                <div className="col-6">
                  <small className="text-muted">Period</small>
                  <p className="fw-bold mb-0">{leave.leavePeriod || "Full Day"}</p>
                </div>
                {leave.isShortLeave && (
                  <div className="col-12">
                    <span className="badge" style={{ background: "#8b5cf6" }}>
                      ⏱ Short Leave — intimation only
                    </span>
                    <span className="ms-2">
                      {leave.shortLeaveFrom} – {leave.shortLeaveTo}
                      {leave.shortLeaveHours ? ` · ${leave.shortLeaveHours}h` : ""}
                    </span>
                  </div>
                )}
                <div className="col-6">
                  <small className="text-muted">From</small>
                  <p className="fw-bold mb-0">{leave.leaveFrom?.split("T")[0]}</p>
                </div>
                <div className="col-6">
                  <small className="text-muted">To</small>
                  <p className="fw-bold mb-0">{leave.leaveTo?.split("T")[0]}</p>
                </div>
                <div className="col-6">
                  <small className="text-muted">Total Days</small>
                  <p className="fw-bold mb-0">{leave.totalLeaves}</p>
                </div>
                <div className="col-6">
                  <small className="text-muted">Applied On</small>
                  <p className="fw-bold mb-0">{leave.created?.split("T")[0]}</p>
                </div>
                {leave.desc && (
                  <div className="col-12">
                    <small className="text-muted">Reason</small>
                    <p className="mb-0">{leave.desc}</p>
                  </div>
                )}
                {leave.adminRemarks && (
                  <div className="col-12">
                    <small className="text-muted">Admin Remarks</small>
                    <p className="mb-0">{leave.adminRemarks}</p>
                  </div>
                )}
                {leave.createdByAdmin && (
                  <div className="col-12">
                    <span className="badge bg-info">Marked by admin</span>
                  </div>
                )}
              </div>

              <div className="mt-4 d-flex gap-2 flex-wrap">
                {isAdmin && leave.status === "pending" && (
                  <>
                    <button className="btn btn-success" onClick={() => handleStatus("approved")}>Approve</button>
                    <button className="btn btn-danger"  onClick={() => handleStatus("rejected")}>Reject</button>
                  </>
                )}
                {leave.status === "pending" && (
                  <button className="btn btn-outline-danger" onClick={handleCancel}>Cancel Application</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Leave period calendar with attendance overlay ───────── */}
        <div className="col-xl-5">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Leave Period & Attendance</h6>
            </div>
            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                {calDays.map(({ date, key, isWeekend }) => {
                  const att = attendance[key];
                  const wasPresent = att && att.punchInTime;
                  const isHalfDay = att?.status === "half-day";

                  return (
                    <div
                      key={key}
                      style={{
                        width: 80,
                        borderRadius: 8,
                        overflow: "hidden",
                        border: `2px solid ${wasPresent ? (isHalfDay ? "#f6a821" : "#2bc155") : isWeekend ? "#ccc" : "#3f9ae0"}`,
                        background: "#fff",
                        boxShadow: wasPresent ? "0 1px 4px rgba(0,0,0,.12)" : "none",
                      }}
                    >
                      {/* Date header */}
                      <div
                        style={{
                          background: wasPresent
                            ? isHalfDay
                              ? "linear-gradient(135deg, #3f9ae022 50%, #2bc15522 50%)"
                              : "#2bc15522"
                            : isWeekend ? "#f0f0f0" : "#3f9ae022",
                          padding: "4px 6px",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{date.getUTCDate()}</div>
                        <div style={{ fontSize: 10, color: "#555" }}>
                          {date.toLocaleString("default", { weekday: "short", timeZone: "UTC" })}
                        </div>
                        {wasPresent && (
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: 9,
                              fontWeight: 700,
                              color: "#fff",
                              background: isHalfDay ? "#f6a821" : "#2bc155",
                              borderRadius: 3,
                              padding: "1px 4px",
                              marginTop: 2,
                            }}
                          >
                            {isHalfDay ? "HALF DAY" : "PRESENT"}
                          </span>
                        )}
                      </div>

                      {/* Selfies (only if employee punched in) */}
                      {wasPresent && (
                        <div style={{ padding: "4px 6px", display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                          {att.selfieUrl && (
                            <div style={{ textAlign: "center" }}>
                              <SelfieThumb url={att.selfieUrl} label={`In selfie · ${key}`} />
                              <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>In</div>
                            </div>
                          )}
                          {att.punchOutSelfieUrl && (
                            <div style={{ textAlign: "center" }}>
                              <SelfieThumb url={att.punchOutSelfieUrl} label={`Out selfie · ${key}`} />
                              <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>Out</div>
                            </div>
                          )}
                          {att.punchInTime && (
                            <div style={{ width: "100%", textAlign: "center", fontSize: 9, color: "#666", marginTop: 2 }}>
                              {new Date(att.punchInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              {att.punchOutTime && (
                                <> – {new Date(att.punchOutTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="d-flex gap-3 mt-3 flex-wrap" style={{ fontSize: 11 }}>
                <span className="d-flex align-items-center gap-1">
                  <span style={{ width: 10, height: 10, background: "#3f9ae0", borderRadius: 2 }} /> On Leave
                </span>
                <span className="d-flex align-items-center gap-1">
                  <span style={{ width: 10, height: 10, background: "#2bc155", borderRadius: 2 }} /> Present
                </span>
                <span className="d-flex align-items-center gap-1">
                  <span style={{
                    width: 10, height: 10, borderRadius: 2,
                    background: "linear-gradient(135deg,#3f9ae0 50%,#2bc155 50%)",
                  }} /> Half Day
                </span>
                <span className="d-flex align-items-center gap-1">
                  <span style={{ width: 10, height: 10, background: "#e0e0e0", borderRadius: 2 }} /> Weekend
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeaveDetail;
